/**
 * ImageProgress Component
 *
 * Progress indicator for image generation with elapsed time tracking.
 */

import { memo } from 'react';
import { Zap } from 'lucide-react';
import { ElapsedTimeDisplay } from '@/components/common';
import { useTranslation } from '../../hooks/useTranslation';

interface ImageProgressProps {
  /** Current progress percentage (0-100) */
  progress: number;
  /** Status message to display */
  message?: string;
  /** ISO timestamp when the job started */
  startedAt?: string | null;
  /** Elapsed seconds from server */
  elapsedSeconds?: number | null;
  /** Estimated remaining seconds (computed from estimator when omitted) */
  estimatedRemainingSeconds?: number | null;
  /** Client-estimated total seconds, used to derive ETA + warning copy */
  expectedSeconds?: number | null;
  /** True when provider may cold-start (Fooocus RunPod / local GPU) */
  mayColdStart?: boolean;
}

export const ImageProgress = memo(function ImageProgress({
  progress,
  message,
  startedAt,
  elapsedSeconds,
  estimatedRemainingSeconds,
  expectedSeconds,
  mayColdStart = false,
}: ImageProgressProps) {
  const { t } = useTranslation();

  // Queue warning only matters when provider may actually cold-start.
  // For warm public RunPod endpoints the bar fills in seconds; no need to
  // tell the user about GPU queueing.
  const isInQueue = mayColdStart && progress <= 15;

  // Derive ETA from elapsed + expected when the server doesn't supply one.
  const computedRemaining =
    estimatedRemainingSeconds ??
    (expectedSeconds && elapsedSeconds !== undefined && elapsedSeconds !== null
      ? Math.max(0, expectedSeconds - elapsedSeconds)
      : null);

  // Get display message
  const displayMessage =
    message ||
    t('image.progress.message', {
      defaultValue: 'Creating your images with AI...',
    });

  return (
    <div className="card p-4 sm:p-6" data-testid="image-progress-container">
      <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100 mb-4 flex items-center space-x-2">
        <Zap className="h-5 w-5 text-amber-600 animate-pulse" />
        <span>{t('image.sections.generating', { defaultValue: 'Generating Images...' })}</span>
      </h3>

      <div className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-secondary-600 dark:text-secondary-400">
            <span>{t('common.progress')}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-3 overflow-hidden">
            <div
              className="bg-gradient-to-r from-amber-500 to-orange-600 h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Status Message */}
        <div className="flex items-center space-x-2 text-secondary-700 dark:text-secondary-300">
          <div className="spinner" />
          <span>{displayMessage}</span>
        </div>

        {/* Elapsed Time Display */}
        {(startedAt || elapsedSeconds !== undefined) && (
          <div className="mt-3">
            <ElapsedTimeDisplay
              startedAt={startedAt ?? null}
              serverElapsedSeconds={elapsedSeconds ?? null}
              estimatedRemainingSeconds={computedRemaining}
              showEstimate={!isInQueue}
              size="sm"
            />
          </div>
        )}

        {/* Cold-start warning only for providers that scale to zero. */}
        {isInQueue && (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            {t('image.progress.queueWarning', {
              defaultValue: 'Queue time varies based on GPU availability. Typically 10-60 seconds.',
            })}
          </p>
        )}
      </div>
    </div>
  );
});
